'use client';

import React, { useCallback, useEffect, type AnchorHTMLAttributes } from 'react';
import clsx from 'clsx';
import { format } from 'url';
import { usePathname, useRouter } from 'next/navigation';
import type { LinkProps as NextLinkProps } from 'next/link';
import NextLink, { useLinkStatus } from 'next/link';
import { usePageTransitionLinkPending } from '@packages/shared/src/providers/PageTransitionProvider/usePageTransition';
import { localizeUrl } from '@packages/shared/src/utils/localizeUrl/localizeUrl';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { useCookies } from '@packages/shared/src/providers/CookieProvider/CookieProvider';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useLocalizedPageUrlDispatch } from '@packages/shared/src/providers/LocalizedPageUrlProvider/useLocalizedPageUrl';
import { type Language } from '@packages/config';
import { isExternalUrl } from './isExternalUrl';

export type LinkProps = NextLinkProps & {
  children?: React.ReactNode;
  className?: string;
  language?: Language;
} & React.RefAttributes<HTMLAnchorElement> &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>;

const PageTransition = () => {
  const { pending } = useLinkStatus();
  const setLinkPending = usePageTransitionLinkPending();

  useEffect(() => {
    setLinkPending(pending);
  }, [pending, setLinkPending]);

  return null;
};
export const Link = (
  props: {
    activeClassName?: string;
    openInNewTab?: boolean;
  } & LinkProps,
) => {
  const {
    activeClassName = 'active',
    className,
    href,
    openInNewTab,
    children,
    language,
    ref,
    prefetch = false,
    onClick,
    ...other
  } = props;
  const config = useConfig();
  const { language: actualLanguage } = useI18n();
  const pathname = usePathname();
  const setUrl = useLocalizedPageUrlDispatch();
  const { getCookies } = useCookies();
  const { appView } = getCookies();
  const isApp = appView === 'true';
  const router = useRouter();

  // it is possible that href is undefined, even though the type definitions do not allow it
  // e.g. when spreading props from a component that passes href conditionally
  // if the format function is called with undefined the whole application crashes
  const linkTarget = typeof href === 'undefined' ? '/' : href;
  const stringifiedHref = typeof linkTarget === 'string' ? linkTarget : format(linkTarget);

  const target = openInNewTab ? '_blank' : '_self';

  const classNames = clsx(className, {
    // startsWith instead of equality to avoid false negatives with query params, and work around trailing slash inconsistencies between server and client
    [activeClassName]: pathname && stringifiedHref.startsWith(pathname) && activeClassName,
  });

  // in cases where the link is intentionally for a different language, the `language` prop has to be used
  const targetLanguage = language ?? actualLanguage;
  // always add the correct language prefix to the href
  const localizedHref =
    // ignore some cases of hrefs for backwards compatibility
    stringifiedHref.startsWith('/')
      ? localizeUrl(stringifiedHref, targetLanguage, config)
      : stringifiedHref;

  // For internal links, strip query params from href and store in data-params.
  // This prevents search engines from crawling and indexing parameterized URLs
  // (tracking params, promo codes, etc.) which would waste crawl budget on
  // duplicate pages. JS appends params back to the URL on click.
  const external = isExternalUrl(stringifiedHref, config.host.domain);
  const qIdx = !external ? localizedHref.indexOf('?') : -1;
  const queryParams = qIdx !== -1 ? localizedHref.slice(qIdx + 1) : undefined;
  const cleanHref = qIdx !== -1 ? localizedHref.slice(0, qIdx) : localizedHref;

  const handleParamClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (onClick) {
        onClick(e);
        if (e.defaultPrevented) return;
      }
      e.preventDefault();
      if (href) setUrl(href.toString());
      router.push(`${cleanHref}?${queryParams}`);
    },
    [cleanHref, queryParams, router, onClick, href, setUrl],
  );

  // if we are in an appview, we can not render the next link. we need to use an default anchor so the redirect is not done via javascript
  if (isApp) {
    return (
      <a
        className={className}
        href={stringifiedHref}
        ref={ref}
        target={target}
        onClick={onClick}
        {...other}
      >
        {children}
      </a>
    );
  }

  return (
    <NextLink
      onNavigate={() => href && setUrl(href.toString())}
      data-testid="link"
      role="link"
      href={cleanHref}
      data-params={queryParams}
      ref={ref}
      className={classNames}
      target={target}
      prefetch={prefetch}
      onClick={queryParams ? handleParamClick : onClick}
      {...other}
    >
      <PageTransition />
      {children}
    </NextLink>
  );
};
